import React, { useMemo, useState, useEffect, ChangeEvent, FormEvent, useCallback, useRef } from "react";
import debounce from 'lodash.debounce';  // Import debounce
import { useNavigate, useLocation } from "react-router-dom";
import {
  Col,
  Container,
  Row,
  UncontrolledAccordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem
} from "reactstrap";
import Select from "react-select";
import type { SingleValue } from "react-select";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TabAccessCard from "../../../components/Common/TabAccessCard/TabAccessCard";
import axios from "axios";
import { Form, Input, Label, Card, CardHeader, CardBody, Button, Modal } from "reactstrap";
import "flatpickr/dist/themes/material_blue.css";
import Flatpickr from "react-flatpickr";
import DataTable, { TableStyles } from "react-data-table-component";
import {
  getUsers as onGetUsers,
  updateUser,
} from "../../../slices/thunks";
import { useSelector, useDispatch } from "react-redux";
import { toast } from 'react-toastify';
import { useAgency } from "src/components/Common/agencyBrandingData";
import axiosInstance from "src/common/axiosInstance";
import DeleteModal from "src/common/DeleteModal";
import profile_placeholder from "../../../assets/images/users/profile_placeholder.jpg"
import { RootState } from "../../../slices/store";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { normalizePhoneNumber } from "src/helpers/phoneHelper";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import TableCustomLoader from "src/components/Common/TableCustomLoader";
import UserMetrics from "./UserMetrics";
import "./clientUsers.css"
import useIsMobile from "src/hooks/useIsMobile";
import Pagination from "src/components/Common/Pagination";
import { tabRegistry } from "src/pages/ReportsPage/tabRegistry";
import { formatMetricName, formatMetricString } from "src/helpers/commonHelper";
import {
  CrossSectionTarget,
  MetricsLinkEntry,
  findLinkByWidget,
  findLinkByTabMetric,
  findLinkByExchangeMetric,
  getCrossSectionTargets,
} from "src/helpers/metricsLinkMap";
import CrossSectionSyncModal from "src/components/Common/CrossSectionSyncModal";

interface FormData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  permission: string;
  accountName: string;
  allowOrder: boolean;
  activateUser: boolean;
  profilePic: string;
  mobile: string;
  userMetrics: string;
  exportTabs: string;
  analyticsWidgets: string;
  tabAccess: Record<string, string[]>;
  users_id: number;
  accountId: string;
  profileFile?: File | null;
  accountsArr: any[];
  clientName: string;
  clientId: string;
  ordersArr: any[];
}

interface Account {
  account_id: string;
  account_name: string;
  users_id?: number;
}

interface AlertMessage {
  type: 'success' | 'danger' | 'warning';
  message: string;
}

interface Client {
  client_id: string;
  client_name: string;
  id: number;
  AccountId?: string;
}
type MetricPayload = {
  userId: number | string;
  exchanges: {
    exchangeId: number | string;
    selectedMetrics: string[];
  }[];
};

const AddEditUser: React.FC = (props: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const agency = useAgency();
  const userData = location.state?.user;
  const previousPage = location.state?.from || "/users";
  const isEditMode = !!userData;
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsArr, setAccountsArr] = useState<Account[]>([]);
  const accountsArrRef = useRef<Account[]>([]);
  const [tempAccounts, setTempAccounts] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [clientOptions, setClientOptions] = useState<OptionType[]>([]);
  const [alertMessage, setAlertMessage] = useState<AlertMessage | null>(null);
  const [errors, setErrors] = useState({ firstName: false, lastName: false });
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [accountDelete, setAccountDelete] = useState<Account | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [showAdminConfirmModal, setShowAdminConfirmModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const userState = useSelector((state: RootState) => state.login);

  // Compute which tabs the logged-in user can assign (Client admins only see their own tabs)
  const adminVisibleTabs = useMemo(() => {
    const loggedIn = userState.user?.user;
    const isClient = loggedIn?.user_type === 'Client';
    const adminTabAccess: Record<string, string[]> | null = isClient && loggedIn?.tab_access
      ? (() => { try { return JSON.parse(loggedIn.tab_access) || null; } catch { return null; } })()
      : null;

    return tabRegistry
      .filter(tab => {
        if (tab.tabId === 'main') return false;
        if (adminTabAccess) {
          if (tab.tabId in adminTabAccess) {
            // Explicitly set to empty array = access removed
            if (Array.isArray(adminTabAccess[tab.tabId]) && adminTabAccess[tab.tabId].length === 0) return false;
          } else {
            // Not configured — show only if tab has defaultSelectedMetrics
            if (!tab.defaultSelectedMetrics) return false;
          }
        }
        return true;
      })
      .map(tab => ({
        ...tab,
        effectiveMetrics: adminTabAccess && (tab.tabId in adminTabAccess)
          ? (tab.availableMetrics ?? []).filter(m => adminTabAccess[tab.tabId]?.includes(m.id))
          : (tab.availableMetrics ?? []),
      }));
  }, [userState.user?.user]);
  const adminUser: any = localStorage.getItem("adminUser");
  const tempAuthUser: any = localStorage.getItem("tempAuthUser");
  const [userMetricsPayload, setUserMetricsPayload] = useState<MetricPayload | null>(null);
  const [externalExchangeToggle, setExternalExchangeToggle] = useState<{
    metricId: string;
    action: "select" | "deselect";
    timestamp: number;
    exchangeId?: number;
  } | null>(null);
  const [syncModalState, setSyncModalState] = useState<{
    metricLabel: string;
    action: "select" | "deselect";
    originLabel: string;
    targets: CrossSectionTarget[];
    pendingApply: () => void;
  } | null>(null);
  const [tableLoading, setTableLoading] = useState(false);
  const capitalizeWord = (word: string): string =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

  type OptionType = {
    label: string;
    value: string;
  };

  const permissionOptions: OptionType[] = [
    { value: "Standard", label: "Standard" },
    { value: "Admin", label: "Admin" }
  ];

  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    permission: "",
    profilePic: "",
    accountName: "",
    allowOrder: false,
    activateUser: false,
    mobile: "",
    userMetrics: "",
    exportTabs: "",
    tabAccess: {},
    users_id: 0,
    accountId: "",
    profileFile: null as File | null,
    accountsArr: [],
    clientName: '',
    clientId: '',
    ordersArr: [],
    analyticsWidgets: "Est. Spend,Impressions,Reach,Clicks,AVG. CTR,AVG. CPC,AVG. Conv. Rate,AVG. CPA",
  });

  useEffect(() => {
    if (isEditMode && userData) {
      setFormData((prevState) => ({
        ...prevState,
        firstName: userData.FirstName || "",
        lastName: userData.LastName || "",
        username: userData.name || "",
        email: userData.email || "",
        permission: capitalizeWord(userData.permission_type) || "",
        profilePic: userData.profile_picture_url || "",
        allowOrder: userData.allow_order ? true : false,
        mobile: normalizePhoneNumber(userData?.mobile) || "",
        users_id: userData.users_id || 0,
        activateUser: userData.status == 'Active' ? true : false,
        analyticsWidgets: userData.analytics_widgets
          ? JSON.parse(userData.analytics_widgets).join(',')
          : "Est. Spend,Impressions,Reach,Clicks,AVG. CTR,AVG. CPC,AVG. Conv. Rate,AVG. CPA",
        tabAccess: userData.tab_access ? JSON.parse(userData.tab_access) : {},
      }));
      fetchData();
      // fetchClients(tempAccounts);
    } else {
      // Reset form for Add mode
      setFormData({
        firstName: "",
        lastName: "",
        username: "",
        email: "",
        permission: "",
        profilePic: "",
        accountName: "",
        allowOrder: false,
        activateUser: false,
        mobile: "",
        userMetrics: "Impressions,Clicks,CTR",
        exportTabs: "Line Item,Line Item Creative,Data By Day",
        analyticsWidgets: "Est. Spend,Impressions,Reach,Clicks,AVG. CTR,AVG. CPC,AVG. Conv. Rate,AVG. CPA",
        tabAccess: {},
        users_id: 0,
        accountId: "",
        accountsArr: [],
        clientName: '',
        clientId: '',
        ordersArr: [],
      });
      // Only fetch clients in add mode if agency ID is available
      if (agency.data.Id) {
        fetchClients(agency.data.Id);
      }
    }
  }, [isEditMode, userData]);

  // Whenever `clients` changes, update `clientOptions`
  useEffect(() => {
    if (Array.isArray(clients)) {
      const options = clients.map((client) => ({
        label: client.client_name,
        value: String(client.id),
      }));
      setClientOptions(options);
    }
  }, [clients]);

  const handlePhoneChange = (phone: string, data: any) => {
    setFormData((prev) => ({
      ...prev,
      mobile: phone,
    }));
    setPhoneError(null);
  };

  const validateForm = async (): Promise<boolean> => {
    if (formData.email != "") {
      if (formData.email.length > 200) {
        toast.error("Email address cannot exceed 200 characters.");
        return false;
      }

      try {
        const emailResponse = await axios.post(`${process.env.REACT_APP_LARAVEL_API_URL}/check-email`, {
          email: formData.email,
          users_id: formData.users_id,
        });
        if (emailResponse.data.exists) {
          toast.error('This email is already associated with an existing user.');
          return false;
        }
      } catch (error) {
        console.error('Error checking email:', error);
        toast.error('Unable to validate email. Please try again later.');
        return false;
      }
    }

    if (formData.mobile != "") {
      const phoneNumber = parsePhoneNumberFromString('+' + formData.mobile);
      //check valid format for selected country
      if (!phoneNumber?.isValid()) {
        setPhoneError('Please enter a valid phone number for the selected country.');
        return false;
      }
      setPhoneError(null);
      try {

        const mobileResponse = await axios.post(`${process.env.REACT_APP_LARAVEL_API_URL}/check-mobile`, {
          mobile: formData.mobile,
          users_id: formData.users_id,
        });
        if (mobileResponse.data.exists) {
          toast.error('This mobile number is already associated with an active user.');
          return false;
        }
      } catch (error) {
        console.error('Error checking mobile:', error);
        toast.error('Unable to validate mobile number. Please try again later.');
        return false;
      }

    }
    return true;
  };

  // ── Cross-Section Metric Sync ──

  const MANDATORY_WIDGETS = ["Impressions", "Clicks", "AVG. CTR"];

  const getWidgetCurrentSelected = (): string[] => {
    return formData.analyticsWidgets
      ? formData.analyticsWidgets.split(',').filter(Boolean)
      : [];
  };

  const getTabCurrentMetrics = (tabId: string): string[] => {
    if (tabId in formData.tabAccess) return formData.tabAccess[tabId];
    const tab = adminVisibleTabs.find(t => t.tabId === tabId);
    return tab?.defaultSelectedMetrics ?? [];
  };

  const isTargetActionNeeded = (
    target: CrossSectionTarget,
    action: "select" | "deselect"
  ): boolean => {
    if (target.section === "widget") {
      const current = getWidgetCurrentSelected();
      const has = current.includes(target.metricId);
      return action === "deselect" ? has : !has;
    }
    if (target.section === "tab") {
      const current = getTabCurrentMetrics(target.detail!);
      const has = current.includes(target.metricId);
      return action === "deselect" ? has : !has;
    }
    // exchange — always show, externalToggle handles shouldToggle guard
    return true;
  };

  const attemptCrossSectionSync = (
    originSection: "widget" | "tab" | "exchange",
    metricId: string,
    action: "select" | "deselect",
    pendingApply: () => void,
    originDetail?: string
  ) => {
    let link: MetricsLinkEntry | undefined;
    if (originSection === "widget") link = findLinkByWidget(metricId);
    else if (originSection === "tab") link = findLinkByTabMetric(metricId);
    else link = findLinkByExchangeMetric(metricId);

    if (!link) {
      pendingApply();
      return;
    }

    let targets = getCrossSectionTargets(link, originSection, originDetail);

    // Filter targets where action is deselect + mandatory widget
    if (action === "deselect") {
      targets = targets.filter(t =>
        !(t.section === "widget" && MANDATORY_WIDGETS.includes(t.metricId))
      );
    }

    // Filter tabs not visible to admin
    targets = targets.filter(t => {
      if (t.section === "tab") {
        return adminVisibleTabs.some(tab => tab.tabId === t.detail);
      }
      return true;
    });

    // Filter exchange targets if payload not loaded
    if (!userMetricsPayload) {
      targets = targets.filter(t => t.section !== "exchange");
    }

    // Filter targets where the action wouldn't produce a change
    targets = targets.filter(t => isTargetActionNeeded(t, action));

    if (targets.length === 0) {
      pendingApply();
      return;
    }

    setSyncModalState({
      metricLabel: formatMetricName(metricId),
      action,
      originLabel: originSection === "widget"
        ? "Analytics Widgets"
        : originSection === "tab"
          ? `Report Tabs — ${adminVisibleTabs.find(t => t.tabId === originDetail)?.tabLabel ?? originDetail}`
          : "Exchange Metrics",
      targets,
      pendingApply,
    });
  };

  const handleSyncConfirm = (selectedTargets: CrossSectionTarget[]) => {
    if (!syncModalState) return;
    const { action, pendingApply } = syncModalState;

    // Apply origin change
    pendingApply();

    // Apply to checked targets
    for (const target of selectedTargets) {
      if (target.section === "widget") {
        setFormData(prev => {
          const current = prev.analyticsWidgets ? prev.analyticsWidgets.split(',').filter(Boolean) : [];
          const updated = action === "select"
            ? (current.includes(target.metricId) ? current : [...current, target.metricId])
            : current.filter(w => w !== target.metricId);
          return { ...prev, analyticsWidgets: updated.join(',') };
        });
      } else if (target.section === "tab") {
        setFormData(prev => {
          const tabId = target.detail!;
          const current = tabId in prev.tabAccess
            ? prev.tabAccess[tabId]
            : (adminVisibleTabs.find(t => t.tabId === tabId)?.defaultSelectedMetrics ?? []);
          const updated = action === "select"
            ? (current.includes(target.metricId) ? current : [...current, target.metricId])
            : current.filter(m => m !== target.metricId);
          return { ...prev, tabAccess: { ...prev.tabAccess, [tabId]: updated } };
        });
      } else if (target.section === "exchange") {
        setExternalExchangeToggle({
          metricId: target.metricId,
          action,
          timestamp: Date.now(),
        });
      }
    }

    setSyncModalState(null);
  };

  const handleSyncDismiss = () => {
    if (!syncModalState) return;
    syncModalState.pendingApply();
    setSyncModalState(null);
  };

  // Handle input and checkbox changes
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, type, value } = e.target;
    let newValue = value;

    if (name === "allowOrder") {
      if (!formData.allowOrder) {
        // Turning ON — fetch orders
        fetchOrders(
          formData.accountsArr.length > 0 ? formData.accountsArr : undefined,
          formData.clientId || undefined
        );
      } else {
        // Turning OFF — clear selections
        setSelectedOrders([]);
        setFormData(prev => ({ ...prev, ordersArr: [] }));
      }
    }

    if (name === "firstName" || name === "lastName") {
      let sanitizedValue = value.replace(/[^a-zA-Z\s]/g, "");

      if (sanitizedValue.length > 50) {
        sanitizedValue = sanitizedValue.slice(0, 50);
      }

      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
      return;
    }

    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      const { checked } = e.target; // Safe to access 'checked' here
      const [section, field] = name.split(".");
      if (section && field) {
        setFormData((prev) => ({
          ...prev,
          [section]: {
            ...(prev as any)[section],
            [field]: checked,
          },
        }));
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      const { value } = e.target;
      setFormData({ ...formData, [name]: value });
    }

    setErrors({ ...errors, [name]: value.trim() === "" });
  };

  const searchAccount = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target;
    setSearchValue(value);
    fetchAccounts(value);
  };

  const authUser = useMemo(() => {
    const user = localStorage.getItem("authUser");
    return user ? JSON.parse(user) : null;
  }, []);

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!userMetricsPayload || userMetricsPayload.exchanges.every(ex => ex.selectedMetrics.length === 0)) {
      toast.error("Please select at least one metric before saving the user.");
      return; // stop submission
    }
    setLoading(true);
    const formDataToSend = new FormData();
    const accountIdToSend = agency?.data?.Id ?? tempAccounts;
    formDataToSend.append("fname", formData.firstName);
    formDataToSend.append("lname", formData.lastName);
    formDataToSend.append("username", formData.username);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("permission", String(formData.permission));
    formDataToSend.append("accountName", formData.accountName);
    formDataToSend.append("allowOrder", String(formData.allowOrder));
    formDataToSend.append("activateUser", String(formData.activateUser));
    formDataToSend.append("mobile", formData.mobile);
    formDataToSend.append("uds_metrics", JSON.stringify(userMetricsPayload));
    formDataToSend.append("userMetrics", String(formData.userMetrics));
    formDataToSend.append("exportTabs", String(formData.exportTabs));
    formDataToSend.append("analyticsWidgets", String(formData.analyticsWidgets));
    formDataToSend.append("tabAccess", JSON.stringify(formData.tabAccess));
    formDataToSend.append("users_id", String(formData?.users_id));
    formDataToSend.append("accountId", accountIdToSend);
    formDataToSend.append("account_arr", JSON.stringify(formData.accountsArr));
    formDataToSend.append("owner_id", String(authUser.user.Id));
    formDataToSend.append("client_name", String(formData.clientName));
    formDataToSend.append("client_id", String(formData.clientId));
    formDataToSend.append("ordersArr", JSON.stringify(selectedOrders));
    if (formData.profileFile) {
      formDataToSend.append("profileFile", formData.profileFile); // Append the file
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API_URL;
    setErrors({
      firstName: formData.firstName.trim() === "",
      lastName: formData.lastName.trim() === "",
    });

    // Validate email format before making API call
    if (!formData.email.match(/^[\w.-]+@([\w-]+\.)+[a-zA-Z]{2,}$/)) {
      toast.error('Please enter a valid email address.');
      setLoading(false);
      return false;
    }

    const isValid = await validateForm();
    if (!isValid) {
      setLoading(false);
      return;
    }

    if (formData.firstName == '' || formData.lastName == '') {
      const error = formData.firstName == '' ? "First Name is required" : "Last Name is required";
      toast.error(error);
      setLoading(false);
      return;
    }
    const userType = userState.user.user.user_type;

    // Only enforce account selection for admins who can see account field
    if (["admin", "AE", "AO"].includes(userType)) {
      if (formData.accountsArr.length < 1) {
        toast.error("Please select at least one account.");
        setLoading(false);
        return;
      }
    }
    try {
      dispatch(updateUser(formDataToSend)).then((response: any) => {
        if (response.meta?.requestStatus === "fulfilled") {
          const message = response.payload?.message;
          toast.success(message);
          previousPage == '/team' ? navigate("/team") : navigate("/user-list");
        } else {
          console.error("Error:", response.payload || response.error); // error object
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
      alert('Something went wrong. Please try again later.');
    }
  };

  const confirmAdminPermission = () => {
    setShowAdminConfirmModal(false);
    setFormData((prev) => ({ ...prev, permission: "Admin", clientId: '', clientName: '' }));
    if (formData.allowOrder) {
      fetchOrders(formData.accountsArr.length > 0 ? formData.accountsArr : undefined, undefined);
    }
  };

  const dismissAdminConfirm = () => {
    setShowAdminConfirmModal(false);
  };

  const fetchData = async (extraAccounts: Account[] = []) => {
    if (!userData) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_LARAVEL_API_URL}/get-data`, {
        params: {
          id: userData.users_id,
          current_user_type: userState.user.user.user_type,
          current_agency_id: agency. data.Id,
        },
      });

      const resData = response.data.data || response.data;
      const accounts = resData.accounts;
      const isAdmin = userData.permission_type?.toLowerCase() === 'admin';
      const clientName = isAdmin ? '' : (resData.client_name || '');
      const clientId = isAdmin ? '' : (resData.client_id || '');
      const allAccounts = [...accounts, ...extraAccounts];

      setFormData((prev) => ({
        ...prev,
        accountsArr: allAccounts,
        clientName: clientName,
        clientId: clientId
      }));
      fetchClients(allAccounts);
      if (userData.allow_order)
        fetchOrders(allAccounts, clientId || undefined);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    accountsArrRef.current = formData.accountsArr;
  }, [formData.accountsArr]);

  const fetchAccounts = useCallback(
    debounce(async (query: string) => {
      if (query.length < 3) {
        setAccounts([]);
        return;
      }
      try {
        const response = await axiosInstance.get(`/accounts?search`, {
          params: {
            acc: query,
          },
        });
        const filteredNewAccounts = (response.data.accounts as Account[]).filter(
          (newAcc: Account) => !accountsArrRef.current.some((existingAcc: Account) => existingAcc.account_id == newAcc.account_id)
        );
        setAccounts(filteredNewAccounts);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    }, 500),
    []);

  const handleSelectChange = (
    selected: OptionType | null,
    fieldName: string
  ) => {
    const value = selected?.value || "";

    if (fieldName === "permission" && value === "Admin") {
      const loggedInUser = userState.user.user;
      const isAgencyAdmin = loggedInUser?.user_type === "Client" && loggedInUser?.permission_type === "admin";
      if (isAgencyAdmin) {
        setShowAdminConfirmModal(true);
        return;
      }
      setFormData((prev) => ({ ...prev, permission: value, clientId: '', clientName: '' }));
      if (formData.allowOrder) {
        fetchOrders(formData.accountsArr.length > 0 ? formData.accountsArr : undefined, undefined);
      }
      return;
    }

    if (fieldName === "client") {
      const matchedClient = clients.find((c) => String(c.id) === value);
      handleClientSelect(matchedClient || { client_name: "", id: "" });
    } else {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [fieldName]: value.trim() === "",
      }));
    }
  };

  const handleClientSelect = (client: any) => {
    setFormData((prev) => ({
      ...prev,
      clientName: client.client_name,
      clientId: client.id,
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, type, value } = e.target;
    if (e.target instanceof HTMLInputElement && e.target.type === "file") {
      const { files } = e.target;

      if (files && files[0]) {
        const file = files[0];
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(file.type)) {
          toast.error("Invalid file type. Only JPG,JPEG and PNG are allowed.");
          setFormData({ ...formData, profilePic: "" });
          return;
        }

        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
          const maxWidth = 500;
          const maxHeight = 500;
          if (img.width != maxWidth || img.height != maxHeight) {
            toast.error(`Profile picture dimensions should be ${maxWidth}px * ${maxHeight}px.`);
            setFormData({ ...formData, profilePic: "" });
            return;
          }
          const convertedFile = new File([file], file.name, { type: file.type });
          setFormData((prevData) => ({
            ...prevData,
            profilePic: URL.createObjectURL(file),
            profileFile: convertedFile,
          }));
        };
      }
    }
  };

  const columns = [
    {
      name: "Account Name",
      selector: (row: any) => row.account_name,
      sortable: true,
      width: "150px",
      cell: (row: any) => (
        <div
          className="addedit-cell-ellipsis"
          title={row.account_name}
        >
          {row.account_name}
        </div>
      )
    },
    {
      name: "Action",
      cell: (row: any) => (
        <div className="d-flex">
          <button
            className="addedit-action-btn"
            onClick={() => {
              setAccountDelete(row);
              setDeleteModal(true);
            }}
          >
            <i className="uil uil-trash-alt"></i>
          </button>
        </div>
      ),
    },
  ];

  const customStyles: TableStyles = {

    headCells: {
      style: {
        borderRadius: "4px"
      },
    }
  };

  const [sortedOrders, setSortedOrders] = useState<any[]>([]);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const checked = formData.ordersArr.filter((order: any) => order.map_id !== null);
    const unchecked = formData.ordersArr.filter((order: any) => order.map_id === null);
    setSortedOrders([...checked, ...unchecked]);
    setCurrentPage(1);
    setSearchTerm("");

    if (isEditMode) {
      setSelectedOrders(checked.map((order: any) => order.sf_id));
    } else {
      setSelectedOrders([]);
    }
  }, [formData.ordersArr, isEditMode]);

  const displayOrders = useMemo(() => {
    const filtered = sortedOrders.filter((order: any) =>
      order.order_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.account_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.client_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!sortColumn) return filtered;

    const parseDate = (d: string) => {
      if (!d) return '';
      const [mm, dd, yyyy] = d.split('-');
      return `${yyyy}-${mm}-${dd}`;
    };

    const getValue = (order: any) => {
      switch (sortColumn) {
        case 'order_name':   return order.order_name || '';
        case 'account_name': return order.account_name || '';
        case 'client_name':  return order.client_name || '';
        case 'start_date':   return parseDate(order.start_date);
        case 'end_date':     return parseDate(order.end_date);
        default:             return '';
      }
    };

    return [...filtered].sort((a, b) => {
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortedOrders, searchTerm, sortColumn, sortDir]);

  const handleSort = useCallback((column: any, direction: 'asc' | 'desc') => {
    setSortColumn(column.id);
    setSortDir(direction);
    setCurrentPage(1);
  }, []);

  const handleAccountSelect = (account: any) => {
    const updatedAccountsArr = [...formData.accountsArr, account];
    setFormData((prevState) => ({
      ...prevState,
      accountsArr: updatedAccountsArr,
    }));
    setTempAccounts(account.account_id);
    setAccounts([]);
    setSearchValue('');
    fetchClients(account.account_id);

    // If allowOrder is enabled, refresh orders with updated accounts
    if (formData.allowOrder) {
      fetchOrders(updatedAccountsArr, formData.clientId || undefined);
    }
  };

  const handleMultiClientSelect = (selected: any) => {
    const clientIds = selected ? selected.map((s: any) => s.value).join(',') : '';
    const clientNames = selected ? selected.map((s: any) => s.label).join(', ') : '';

    setFormData((prev) => ({
      ...prev,
      clientId: clientIds,
      clientName: clientNames,
    }));

    if (formData.allowOrder) {
      fetchOrders(
        formData.accountsArr.length > 0 ? formData.accountsArr : undefined,
        clientIds || undefined
      );
    }
  };

  const fetchClients = useCallback(async (accountId: string | string[]) => {
  if (!accountId || (typeof accountId === 'string' && accountId === '') || 
      (Array.isArray(accountId) && accountId.length === 0)) {
    return;
  }

  const accountIds = Array.isArray(accountId) ? accountId : [accountId];
  
  try {
    const response = await axiosInstance.get(`${process.env.REACT_APP_LARAVEL_API_URL}/clients`, {
      params: {
        users_id: userData ?  userData.users_id : 0,
        agency_id: accountIds,
        filter_by_user:  true,
        current_user_type: userState.user.user.user_type,
        current_agency_id: agency.data.Id,
        editing_user_id: userData?. users_id,
      },
    });
    
    if (response.data?. clients) {
      const uniqueClients = response.data.clients.filter((client: any, index: any, self: any) =>
        index === self.findIndex((c: any) => c.client_id === client.client_id)
      );
      
      setClients((prevClients) => {
        const merged = [...prevClients, ...uniqueClients];
        return merged.filter(
          (client, index, self) =>
            index === self.findIndex((c) => c.client_id === client.client_id)
        );
      });
    }
  } catch (error) {
    console.error('Error fetching clients:', error);
  }
}, [userData, userState.user.user.user_type, agency.data.Id]);

  useEffect(() => {
    setFormData((prevState) => ({
      ...prevState,
      username: `${prevState.firstName} ${prevState.lastName}`.trim(),
    }));
  }, [formData.firstName, formData.lastName]);

  const handleDeleteAccount = async () => {
    if (formData.accountsArr.length > 0 && accountDelete) {
      try {
        const savedAccountsCount = formData.accountsArr.filter(account => account.users_id).length;

        if (!accountDelete.users_id) {
          const updatedAccountsArr = formData.accountsArr.filter(account => account.account_id !== accountDelete.account_id);

          // Remove only clients belonging to the deleted account
          const deletedClientIds = new Set(
            clients
              .filter(c => c.AccountId === accountDelete.account_id)
              .map(c => String(c.id))
          );
          const remainingClients = clients.filter(c => c.AccountId !== accountDelete.account_id);
          setClients(remainingClients);

          // Keep selected clients from other accounts, remove only those from deleted account
          const currentClientIds = formData.clientId ? formData.clientId.split(',').filter(id => id) : [];
          const updatedClientIds = currentClientIds.filter(id => !deletedClientIds.has(id));
          const updatedClientId = updatedClientIds.join(',');
          const updatedClientName = remainingClients
            .filter(c => updatedClientIds.includes(String(c.id)))
            .map(c => c.client_name)
            .join(', ');

          setFormData(prev => ({
            ...prev,
            accountsArr: updatedAccountsArr,
            clientId: updatedClientId,
            clientName: updatedClientName,
          }));

          // Refresh orders with remaining accounts and remaining selected clients
          if (formData.allowOrder) {
            if (updatedAccountsArr.length === 0) {
              setFormData(prev => ({ ...prev, ordersArr: [] }));
            } else {
              fetchOrders(updatedAccountsArr, updatedClientId || undefined);
            }
          }

          setDeleteModal(false);
          toast.success("Account association removed successfully.");
          return;
        }

        if (savedAccountsCount == 1) {
          setDeleteModal(false);
          toast.error("Please associate another account before removing this or save the recently added changes.");
          return;
        }

        // Preserve any unsaved accounts so they survive the fetchData reload
        const unsavedAccounts = formData.accountsArr.filter(
          a => !a.users_id && a.account_id !== accountDelete.account_id
        );

        await axiosInstance.delete(`/account-association/${accountDelete.account_id}?users_id=${accountDelete.users_id}`);
        setDeleteModal(false);
        toast.success("Account association removed successfully");
        setClients([]);
        setClientOptions([]);
        setFormData(prev => ({ ...prev, clientName: '', clientId: '' }));
        fetchData(unsavedAccounts);
      } catch (error) {
      }
    }
  };

  const fetchOrders = async (accounts?: Account[], clientIds?: string) => {
    setTableLoading(true);
    let accountIds: string[] = [];

    // Priority 1: Use explicitly passed accounts (e.g. from fetchData in edit mode)
    if (accounts && Array.isArray(accounts) && accounts.length > 0) {
      accountIds = accounts.map((account) => account.account_id).filter(Boolean);
    }
    // Priority 2: Use accounts already in formData
    else if (formData.accountsArr && Array.isArray(formData.accountsArr) && formData.accountsArr.length > 0) {
      accountIds = formData.accountsArr.map((account) => account.account_id).filter(Boolean);
    }
    // Priority 3: Fall back to agency ID for admin/client users
    else if (adminUser || tempAuthUser || (userState.user.user.user_type === "Client")) {
      if (agency.data.Id) {
        accountIds = [agency.data.Id];
      }
    }

    if (accountIds.length === 0) {
      setTableLoading(false);
      return;
    }

    try {
      const effectiveClientIds = formData.permission === "Admin" ? undefined : clientIds;
      const response = await axiosInstance.get(`/fetch-orders`, {
        params: {
          accountsArr: accountIds,
          userId: userData ? userData.users_id : formData?.users_id,
          ...(effectiveClientIds ? { clientIds: effectiveClientIds } : {}),
        },
      });
      setFormData((prevData) => ({
        ...prevData,
        ordersArr: response.data.orders,
      }));
      setTableLoading(false);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setTableLoading(false);
    }
  };

  const handleOrderSelection = (orderId: string) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };
  const userInitials = useMemo(() => {
    if (!formData.firstName. trim() || !formData.lastName.trim()) return '';
    
    const firstInitial = formData.firstName. charAt(0).toUpperCase();
    const lastInitial = formData.lastName. charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  }, [formData.firstName, formData.lastName]);

  useEffect(() => {
    if (formData.accountsArr.length === 0) {
      setClients([]);
      setClientOptions([]);
      setFormData(prev => ({ ...prev, clientName: '', clientId: '' }));
    }
  }, [formData.accountsArr]);



  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Stop browser's default "submit on Enter" behavior
    }
  };

  const isMobile = useIsMobile();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(5);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayOrders.slice(indexOfFirstItem, indexOfLastItem);

  const handleSelectAllOnPage = () => {
    const currentPageIds = currentItems.map((order: any) => order.sf_id);
    const allCurrentSelected = currentPageIds.every((id: string) => selectedOrders.includes(id));

    if (allCurrentSelected) {
      // Deselect all on current page
      setSelectedOrders((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      // Select all on current page
      setSelectedOrders((prev) => {
        const newSelected = [...prev];
        currentPageIds.forEach((id: string) => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  };

  const areAllOnPageSelected = useMemo(() => {
    if (currentItems.length === 0) return false;
    return currentItems.every((order: any) => selectedOrders.includes(order.sf_id));
  }, [currentItems, selectedOrders]);

  const orderColumns = [
    {
      name: (
        <input
          type="checkbox"
          checked={areAllOnPageSelected}
          onChange={handleSelectAllOnPage}
          className="form-check-input addedit-order-checkbox"
        />
      ),
      cell: (row: any) => (
        <input
          type="checkbox"
          checked={selectedOrders.includes(row.sf_id)}
          onChange={() => handleOrderSelection(row.sf_id)}
          className="form-check-input addedit-order-checkbox"
        />
      ),
      width: "50px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      id: 'order_name',
      name: "Order Name",
      selector: (row: any) => row.order_name,
      sortable: true,
      width: "300px",
      cell: (row: any) => (
        <div
          className="addedit-cell-ellipsis-clickable"
          title={row.order_name}
        >
          {row.order_name}
        </div>
      )
    },
    {
      id: 'account_name',
      name: "Account Name",
      selector: (row: any) => row.account_name,
      sortable: true,
      width: "250px",
    },
    {
      id: 'client_name',
      name: "Client Name",
      selector: (row: any) => row.client_name || "",
      sortable: true,
      width: "200px",
      cell: (row: any) => (
        <div
          className="addedit-cell-ellipsis"
          title={row.client_name || ""}
        >
          {row.client_name || ""}
        </div>
      ),
    },
    {
      id: 'start_date',
      name: "Start Date",
      selector: (row: any) => row.start_date,
      sortable: true,
      width: "120px",
    },
    {
      id: 'end_date',
      name: "End Date",
      selector: (row: any) => row.end_date,
      sortable: true,
      width: "120px",
    },
  ];

  const handlePageChange = (page: number) => setCurrentPage(page);

  return (
    <div className="page-content addedit-page-content">
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteAccount}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Container fluid className="addedit-container-full">
        {/* Render Breadcrumbs */}
        <Breadcrumbs title="User" breadcrumbItem={isEditMode ? "Edit User" : "Add User"} titleLink={previousPage === '/team' ? "/team" : "/user-list"} />
        <Row>
          <Col md={3}>
            <Card>
              <CardBody>
                <div className="profile-bg position-relative overflow-hidden rounded-top addedit-profile-bg">
                  <div className="bg-overlay bg-dark bg-gradient"></div>
                  <div className="position-relative p-3 text-end">
                  </div>
                </div>
                <div className="text-center">
                  <div className="position-relative d-inline-block mx-auto mt-n5 mb-4">
                    <div className="avatar-lg">
                      {formData.profilePic ?  (
                        <img
                          src={formData.profilePic}
                          alt="Profile"
                          className="rounded-circle img-thumbnail"
                        />
                      ) : userInitials ? (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white img-thumbnail addedit-initials-avatar"
                        >
                          {userInitials}
                        </div>
                      ) : (
                        <img
                          src={profile_placeholder}
                          alt="Profile"
                          className="rounded-circle img-thumbnail"
                        />
                      )}
                    </div>
                    <label htmlFor="profilePicInput" 
                      className="d-block position-absolute profile-pic-edit-btn"
                      title="Profile Picture">
                      <div className="avatar-sm">
                        <div className="avatar-title rounded-circle bg-light text-primary">
                          <i className="mdi mdi-pencil"></i>
                        </div>
                      </div>
                    </label>
                    <input
                      type="file"
                      id="profilePicInput"
                      accept="image/*"
                      className="addedit-hidden-input"
                      onChange={handleFileChange}
                    />
                  </div>
                  <h5 className="font-size-16 mb-1">{formData.username}</h5>
                  <h4 className="font-size-16 mb-1">{userState.user.user.user_type == "Client" ? agency.data.agency_name : formData.accountName}</h4>
                </div>
                <hr className="my-4" />
                <div className="email-mobile-input-div">
                  <div >
                    <p className="text-muted mb-1">Email {" "}:
                      <span className="text-danger">*</span>
                    </p>
                    <input
                      type="email"
                      value={formData.email}
                      name="email"
                      onChange={handleInputChange}
                      className="form-control"
                      id="gen-info-email-input"
                      placeholder="Enter Email"
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <p className="text-muted mb-1">Mobile {" "}:</p>
                    <PhoneInput
                      country="us"
                      value={normalizePhoneNumber(formData.mobile)}
                      onChange={handlePhoneChange}
                      inputStyle={{ width: '100%', height: "38px" }}
                    />
                    {phoneError && (
                      <div className="addedit-phone-error">
                        {phoneError}
                      </div>
                    )}
                  </div>
                </div>
                {formData.accountsArr.length > 0 && ["admin", "AE", "AO"].includes(userState.user.user.user_type) && (
                  <div className={`addedit-accounts-wrapper ${isMobile ? 'addedit-accounts-wrapper-mobile' : 'addedit-accounts-wrapper-desktop'}`}>
                    <DataTable
                      className="data-table"
                      columns={columns}
                      data={formData.accountsArr}
                      highlightOnHover={false}
                      responsive
                      customStyles={customStyles}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col md={9}>
            <Card>
              <CardBody>
                <form onKeyDown={handleKeyDown}>
                    <div>
                      <div className="row row-cols-1 row-cols-sm-2 g-4 mb-3">
                        <div className="col">
                          <label>First Name</label>
                          <span className="text-danger">*</span>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="form-control shadow-sm rounded"
                            id="formrow-firstname-input"
                            placeholder="First Name"
                          />
                          {errors.firstName && <span className="text-danger">First Name is required</span>}
                        </div>
                        <div className="col position-relative">
                          <label>Last Name</label>
                          <span className="text-danger">*</span>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="form-control shadow-sm rounded"
                            id="formrow-lastname-input"
                            placeholder="Last Name"
                          />
                          {errors.lastName && <span className="text-danger">Last Name is required</span>}
                        </div>
                      </div>
                      <div className="row row-cols-1 row-cols-sm-2 g-4 mb-3">
                        <div className="col">
                          <label>Set Permission</label>
                          <Select
                            name="permission"
                            value={
                              permissionOptions.find((o) => o.value === formData.permission)
                              ?? permissionOptions[0] // fallback to first option
                            }
                            onChange={(selected: OptionType | null) =>
                              handleSelectChange(selected, "permission")
                            }
                            options={permissionOptions}
                            className="dropdown custom-select shadow-sm rounded"
                            classNamePrefix="react-select"
                            isSearchable={false}
                          />
                        </div>
                        {["admin", "AE", "AO"].includes(userState.user.user.user_type) ? (
                          <div className="col-12 col-sm-6">
                            <label>Search Account</label>
                          <span className="text-danger">*</span>
                            <input
                              type="text"
                              name="searchAccount"
                              value={searchValue}
                              onChange={searchAccount}
                              className="form-control shadow-sm rounded"
                              placeholder="Type to search..."
                            />
                            {/* Dropdown for matching accounts */}
                            {accounts.length > 0 && (
                              <ul
                                className="accounts-dropdown list-group position-absolute bg-white shadow-sm rounded"
                               
                              >
                                {accounts.map((account) => (
                                  <li
                                    key={account.account_id}
                                    className="list-group-item addedit-account-item"
                                    onClick={() => handleAccountSelect(account)}
                                  >
                                    {account.account_name}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ) : formData.permission !== "Admin" ? (
                          <div className="col select-wrapper">
                            <label htmlFor="client-select">Select Client</label>
                            <Select
                              name="client"
                              value={clientOptions. filter((o) =>
                                formData.clientId. split(',').filter(id => id).includes(o.value)
                              )}
                              onChange={(selected:  any) => handleMultiClientSelect(selected)}
                              options={clientOptions}
                              className="dropdown custom-select shadow-sm rounded"
                              classNamePrefix="react-select"
                              isMulti
                              placeholder="Select Clients"
                              styles={{
                                input: (provided: any) => ({
                                  ...provided,
                                  fontSize: '16px',
                                }),
                                menu: (provided: any) => ({
                                  ...provided,
                                  zIndex: 1050
                                }),
                                menuList: (base:  any) => ({
                                  ...base,
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                }),
                                option: (base: any, state: any) => ({
                                  ...base,
                                  backgroundColor: 'transparent',        
                                  color: 'inherit',                        
                                  ': active': {
                                    backgroundColor: 'transparent',        
                                  },
                                }),
                                valueContainer: (base: any) => ({
                                  ...base,
                                  maxHeight: '81px', // 3 rows: 38px per row
                                  overflowX: 'hidden',
                                  overflowY:  'auto',
                                  flexWrap: 'wrap',
                                  padding: '2px 8px',
                                  ': :-webkit-scrollbar': {
                                    width: '6px',
                                  },
                                  ': :-webkit-scrollbar-track':  {
                                    background: 'transparent',
                                  },
                                  '::-webkit-scrollbar-thumb': {
                                    background:  '#888',
                                    borderRadius: '3px',
                                  },
                                  '::-webkit-scrollbar-thumb:hover': {
                                    background: '#555',
                                  },
                                }),
                                multiValue: (base: any) => ({
                                  ...base,
                                  backgroundColor: 'var(--bs-primary)',
                                  borderRadius: '4px',
                                  flexShrink: 0,
                                }),
                                multiValueLabel: (base: any) => ({
                                  ...base,
                                  color: '#fff',
                                  padding: '2px 6px',
                                  whiteSpace: 'nowrap',
                                }),
                                multiValueRemove:  (base: any) => ({
                                  ...base,
                                  color: '#fff',
                                  ': hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    color: '#fff',
                                    cursor: 'pointer',
                                  },
                                }),
                                control: (base: any) => ({
                                  ...base,
                                  minHeight: '38px',
                                  maxHeight: '86px',
                                }),
                              }}
                            />
                          </div>
                        ) : null}
                      </div>
                      {["admin", "AE", "AO"].includes(userState.user.user.user_type) && formData.permission !== "Admin" && (
                        <div className="row row-cols-1 row-cols-sm-2 g-4 mb-3">
                          <div className="col select-wrapper">
                            <label htmlFor="client-select">Select Client</label>
                            <Select
                              name="client"
                              value={clientOptions.filter((o) => 
                                formData.clientId.split(',').filter(id => id).includes(o.value)
                              )}
                              onChange={(selected: any) => handleMultiClientSelect(selected)}
                              options={clientOptions}
                              className="dropdown custom-select shadow-sm rounded"
                              classNamePrefix="react-select"
                              isMulti
                              placeholder="Select Clients"
                              styles={{
                                input:  (provided: any) => ({
                                  ...provided,
                                  fontSize: '16px',
                                }),
                                menu: (provided: any) => ({
                                  ...provided,
                                  zIndex: 1050
                                }),
                                menuList: (base: any) => ({
                                  ...base,
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                }),
                                option: (base: any, state: any) => ({
                                  ...base,
                                  backgroundColor: 'transparent',        
                                  color: 'inherit',                        
                                  ':active': {
                                    backgroundColor: 'transparent',        
                                  },
                                }),
                                valueContainer: (base: any) => ({
                                  ...base,
                                  maxHeight: '81px', // 3 rows: 38px per row
                                  overflowX: 'hidden',
                                  overflowY: 'auto',
                                  flexWrap:  'wrap',
                                  padding:  '2px 8px',
                                  '::-webkit-scrollbar':  {
                                    width: '6px',
                                  },
                                  '::-webkit-scrollbar-track': {
                                    background: 'transparent',
                                  },
                                  '::-webkit-scrollbar-thumb': {
                                    background: '#888',
                                    borderRadius: '3px',
                                  },
                                  '::-webkit-scrollbar-thumb:hover': {
                                    background: '#555',
                                  },
                                }),
                                multiValue: (base: any) => ({
                                  ...base,
                                  backgroundColor: 'var(--bs-primary)',
                                  borderRadius: '4px',
                                  flexShrink: 0,
                                }),
                                multiValueLabel: (base:  any) => ({
                                  ...base,
                                  color: '#fff',
                                  padding: '2px 6px',
                                  whiteSpace: 'nowrap',
                                }),
                                multiValueRemove:  (base: any) => ({
                                  ...base,
                                  color: '#fff',
                                  ':hover':  {
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    color: '#fff',
                                    cursor: 'pointer',
                                  },
                                }),
                                control: (base: any) => ({
                                  ...base,
                                  minHeight: '38px',
                                  maxHeight: '86px',
                                }),
                              }}
                            />
                          </div>
                          <div className="col position-relative">
                          </div>
                        </div>
                      )}
                    </div>
                  {/* Activate User */}
                  <div className="form-check d-flex d-sm-block  gap-2  mt-3" >
                    <input
                      type="checkbox"
                      name="activateUser"
                      checked={formData.activateUser}
                      onChange={handleInputChange}
                      className="form-check-input"
                    />
                    <label>Activate User (bypass user registration)</label>
                  </div>
                  {/* Section 1: Analytics Widgets */}
                  <div className="card-header d-flex align-items-center section-header-spacing">
                    <div className="flex-shrink-0 me-2">
                      <div className="avatar-sm">
                        <div className="avatar-title rounded-circle btn-primary-subtle text-primary">1</div>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="card-title">Analytics Widgets</h5>
                    </div>
                  </div>
                  {/* @ts-ignore */}
                  <UncontrolledAccordion defaultOpen={['analytics']} stayOpen className="no-border-accordion">
                    <AccordionItem className="mt-3">
                      <AccordionHeader targetId="analytics">
                        <div className="d-flex justify-content-between w-100 align-items-center addedit-accordion-header">
                          <h5 className="card-title addedit-accordion-subtitle">
                            Select Widgets
                          </h5>
                        </div>
                      </AccordionHeader>
                      <AccordionBody accordionId="analytics" className="my-2">
                        <p className="text-muted mb-3 addedit-accordion-description">
                          These settings control the visibility of analytics dashboard widgets.
                        </p>
                        {(() => {
                          const ALL_WIDGET_OPTIONS = [
                            { value: "Impressions",      label: "Impressions" },
                            { value: "Clicks",           label: "Clicks" },
                            { value: "AVG. CTR",         label: "AVG. CTR" },
                            { value: "Est. Spend",       label: "Est. Spend" },
                            { value: "Reach",            label: "Reach" },
                            { value: "AVG. CPC",         label: "AVG. CPC" },
                            { value: "AVG. Conv. Rate",  label: "AVG. CVR" },
                            { value: "AVG. CPA",         label: "AVG. CPA" },
                          ];
                          const loggedInUser = userState.user?.user;
                          const isClientAdmin = loggedInUser?.user_type === 'Client';
                          const adminAllowedWidgets: string[] | null = isClientAdmin && loggedInUser?.analytics_widgets
                            ? JSON.parse(loggedInUser.analytics_widgets)
                            : null;
                          const availableWidgetOptions = adminAllowedWidgets
                            ? ALL_WIDGET_OPTIONS.filter(w => adminAllowedWidgets.includes(w.value))
                            : ALL_WIDGET_OPTIONS;
                          const currentSelected = formData.analyticsWidgets
                            ? formData.analyticsWidgets.split(',').filter(Boolean)
                            : [];
                          return (
                            <Row>
                              {availableWidgetOptions.map((widget) => {
                                const isMandatory = MANDATORY_WIDGETS.includes(widget.value);
                                const isChecked = isMandatory || currentSelected.includes(widget.value);
                                return (
                                  <Col md={3} sm={6} key={widget.value}>
                                    <div className="form-check mb-2">
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`widget-${widget.value}`}
                                        checked={isChecked}
                                        disabled={isMandatory}
                                        onChange={(e) => {
                                          if (isMandatory) return;
                                          const willBeSelected = e.target.checked;
                                          const action = willBeSelected ? "select" : "deselect";
                                          const applyFn = () => {
                                            setFormData(prev => {
                                              const cur = prev.analyticsWidgets ? prev.analyticsWidgets.split(',').filter(Boolean) : [];
                                              const updated = willBeSelected
                                                ? (cur.includes(widget.value) ? cur : [...cur, widget.value])
                                                : cur.filter(w => w !== widget.value);
                                              return { ...prev, analyticsWidgets: updated.join(',') };
                                            });
                                          };
                                          attemptCrossSectionSync("widget", widget.value, action, applyFn);
                                        }}
                                      />
                                      <Label className="form-check-label" htmlFor={`widget-${widget.value}`}>
                                        {widget.label}
                                      </Label>
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          );
                        })()}
                      </AccordionBody>
                    </AccordionItem>
                  </UncontrolledAccordion>

                  {/* Section 2: Report Tab Access — only show when admin has assignable tabs */}
                  {adminVisibleTabs.length > 0 && (
                    <>
                      <div className="card-header d-flex align-items-center section-header-spacing">
                        <div className="flex-shrink-0 me-2">
                          <div className="avatar-sm">
                            <div className="avatar-title rounded-circle btn-primary-subtle text-primary">2</div>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <h5 className="card-title">Report Tab & Export Access</h5>
                        </div>
                      </div>
                      {/* @ts-ignore */}
                      <UncontrolledAccordion defaultOpen={['tabAccess']} stayOpen className="no-border-accordion">
                        <AccordionItem className="mt-3">
                          <AccordionHeader targetId="tabAccess">
                            <div className="d-flex justify-content-between w-100 align-items-center pe-2">
                              <h5 className="card-title tab-access-accordion-title">
                                Tab & Export Visibility
                              </h5>
                            </div>
                          </AccordionHeader>
                          <AccordionBody accordionId="tabAccess" className="my-2">
                            <p className="text-muted mb-3 tab-access-description">
                              Controls which data tabs and their metrics are visible to this user on the Reports page and in Excel exports. Items marked Export Only apply only to the Excel export.
                            </p>
                            <Row>
                              {adminVisibleTabs.map(tab => (
                                <Col lg={6} md={6} xs={12} key={tab.tabId}>
                                  <TabAccessCard
                                    tabId={tab.tabId}
                                    tabLabel={tab.tabLabel}
                                    metrics={tab.effectiveMetrics}
                                    exportOnly={tab.exportOnly}
                                    selectedMetrics={
                                      tab.tabId in formData.tabAccess
                                        ? formData.tabAccess[tab.tabId]
                                        : tab.defaultSelectedMetrics ?? []
                                    }
                                    isEnabled={
                                      tab.tabId in formData.tabAccess
                                        ? formData.tabAccess[tab.tabId].length > 0
                                        : !!tab.defaultSelectedMetrics
                                    }
                                    onChange={(tabId, metrics) => {
                                      const tab = adminVisibleTabs.find(t => t.tabId === tabId);
                                      const allMetricIds = (tab?.effectiveMetrics ?? []).map(m => m.id);

                                      // Tab toggled OFF (empty) or toggled ON / Select All (all metrics) → bulk action
                                      if (
                                        metrics.length === 0 ||
                                        (allMetricIds.length > 0 && allMetricIds.every(id => metrics.includes(id)))
                                      ) {
                                        setFormData(prev => ({
                                          ...prev,
                                          tabAccess: { ...prev.tabAccess, [tabId]: metrics },
                                        }));
                                        return;
                                      }

                                      const current = getTabCurrentMetrics(tabId);
                                      const added = metrics.filter(m => !current.includes(m));
                                      const removed = current.filter(m => !metrics.includes(m));

                                      // Single metric toggle → attempt cross-section sync
                                      if (added.length === 1 && removed.length === 0) {
                                        const metricId = added[0];
                                        const applyFn = () => {
                                          setFormData(prev => ({
                                            ...prev,
                                            tabAccess: { ...prev.tabAccess, [tabId]: metrics },
                                          }));
                                        };
                                        attemptCrossSectionSync("tab", metricId, "select", applyFn, tabId);
                                        return;
                                      }
                                      if (removed.length === 1 && added.length === 0) {
                                        const metricId = removed[0];
                                        const applyFn = () => {
                                          setFormData(prev => ({
                                            ...prev,
                                            tabAccess: { ...prev.tabAccess, [tabId]: metrics },
                                          }));
                                        };
                                        attemptCrossSectionSync("tab", metricId, "deselect", applyFn, tabId);
                                        return;
                                      }

                                      // Bulk action (Select All / Clear All / tab toggle) → apply directly
                                      setFormData(prev => ({
                                        ...prev,
                                        tabAccess: { ...prev.tabAccess, [tabId]: metrics },
                                      }));
                                    }}
                                  />
                                </Col>
                              ))}
                            </Row>
                          </AccordionBody>
                        </AccordionItem>
                      </UncontrolledAccordion>
                    </>
                  )}

                  {/* Section: Report Metrics */}
                  <div className="card-header d-flex align-items-center section-header-spacing">
                    <div className="flex-shrink-0 me-2">
                      <div className="avatar-sm">
                        <div className="avatar-title rounded-circle btn-primary-subtle text-primary">
                          {adminVisibleTabs.length > 0 ? 3 : 2}
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="card-title">Report Exchange Metrics</h5>
                    </div>
                  </div>
                  {/* @ts-ignore */}
                  <UncontrolledAccordion defaultOpen={['2']} stayOpen className="no-border-accordion">
                    {/* ADMIN USERS */}
                    <AccordionItem className="mt-3">
                      <AccordionHeader targetId="2">
                        <div className="d-flex justify-content-between w-100 align-items-center addedit-accordion-header">
                          <h5 className="card-title addedit-accordion-subtitle">
                            Select Metrics
                          </h5>
                        </div>
                      </AccordionHeader>
                      <AccordionBody accordionId="2" className="my-2">
                        <Row>
                          <UserMetrics
                            userId={userData?.users_id}
                            onMetricsChange={(payload: MetricPayload) => setUserMetricsPayload(payload)}
                            interceptToggle={(exchangeId, metricId, willBeSelected) => {
                              const action = willBeSelected ? "select" : "deselect";
                              const applyFn = () => {
                                setExternalExchangeToggle({
                                  metricId,
                                  action,
                                  timestamp: Date.now(),
                                  exchangeId,
                                });
                              };
                              attemptCrossSectionSync("exchange", metricId, action, applyFn);
                            }}
                            externalToggle={externalExchangeToggle}
                          />
                        </Row>
                      </AccordionBody>
                    </AccordionItem>
                  </UncontrolledAccordion>

                  {/* Section: Others */}
                  <div className="card-header d-flex align-items-center section-header-spacing">
                    <div className="flex-shrink-0 me-2">
                      <div className="avatar-sm">
                        <div className="avatar-title rounded-circle btn-primary-subtle text-primary">
                          {adminVisibleTabs.length > 0 ? 4 : 3}
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="card-title">Others</h5>
                    </div>
                  </div>
                  <CardBody className="p-2">
                    <Row>
                      {/* Campaign Permissions */}
                      <div className="form-check form-switch my-3" dir="ltr">
                        <Input type="checkbox" name="allowOrder" className="form-check-input" id="customSwitchsizesm" onChange={handleInputChange} checked={formData.allowOrder} />
                        <Label className="form-check-label" htmlFor="customSwitchsizesm">Allow Campaign Permissions</Label>
                      </div>

                      {formData.allowOrder && (
                        <div className="addedit-campaign-wrapper">
                          <h6>
                            Select Campaign Permissions Below{" "}
                            <span className="addedit-campaign-warning">
                              (Leave all unchecked for user to see all campaigns in their login)
                            </span>
                          </h6>

                          {tableLoading ? (
                              <TableCustomLoader length={1} message="Fetching the orders data" />
                          ) : formData.ordersArr.length > 0 ? (
                            <>
                              <div className="d-flex justify-content-end flex-column flex-md-row my-2">
                                <Input
                                  type="text"
                                  placeholder="Search..."
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}

                                  className="orders-search-input"
                                />
                                {isMobile && (
                                  <div className="d-flex align-items-center gap-2 mt-2">
                                    <input
                                      type="checkbox"
                                      id="mobile-select-all"
                                      checked={areAllOnPageSelected}
                                      onChange={handleSelectAllOnPage}
                                      className="form-check-input addedit-order-checkbox"
                                    />
                                    <label htmlFor="mobile-select-all" className="mb-0 addedit-mobile-select-label">
                                      Select All
                                    </label>
                                  </div>
                                )}
                              </div>
                              {!isMobile ?
                                <DataTable
                                  className="data-table"
                                  columns={orderColumns}
                                  data={currentItems}
                                  highlightOnHover={false}
                                  responsive
                                  customStyles={customStyles}
                                  sortServer
                                  onSort={handleSort}
                                />
                                :
                                <div className="d-block d-md-none mobile-cards-container">
                                  {currentItems.map((order: any) => {
                                    return (
                                      <div className="mobile-card card position-relative gap-4" key={order.sf_id}>
                                        {/* Checkbox at top-right */}
                                        <input
                                          type="checkbox"
                                          checked={selectedOrders.includes(order.sf_id)}
                                          onChange={() => handleOrderSelection(order.sf_id)}
                                          className="form-check-input position-absolute addedit-mobile-card-checkbox"
                                        />
                                        <div
                                          className="orders-mobile-card-row"
                                        >
                                          <span className="row-title fw-bold">
                                            Order Name
                                          </span>
                                          <span className="row-value">
                                            {order.order_name}
                                          </span>
                                        </div>
                                        <div
                                          className="orders-mobile-card-row "
                                        >
                                          <span className="row-title fw-bold">
                                            Account Name
                                          </span>
                                          <span className="row-value">
                                            {order.account_name}
                                          </span>
                                        </div>
                                        <div
                                          className="orders-mobile-card-row "
                                        >
                                          <span className="row-title fw-bold">
                                            Client Name
                                          </span>
                                          <span className="row-value">
                                            {order.client_name || ""}
                                          </span>
                                        </div>
                                        <div
                                          className="orders-mobile-card-row "

                                        >
                                          <span className="row-title fw-bold">
                                            Start Date
                                          </span>
                                          <span className="row-value">
                                            {order.start_date}
                                          </span>

                                        </div>
                                        <div
                                          className="orders-mobile-card-row "

                                        >
                                          <span className="row-title fw-bold">
                                            End Date
                                          </span>
                                          <span className="row-value">
                                            {order.end_date}
                                          </span>

                                        </div>


                                      </div>
                                    );
                                  })}
                                </div>
                              }
                              <Pagination
                                totalPages={Math.ceil(displayOrders.length / itemsPerPage)}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                currentCount={currentItems.length}
                                pageSize={itemsPerPage}
                                onPageSizeChange={(size: number) => {
                                  setItemsPerPage(size);
                                  setCurrentPage(1);
                                }}
                                pageSizeOptions={[10, 20, 30, 50, 70, 100]}
                              />
                            </>
                          ) : (
                            <div className="d-flex justify-content-center align-items-center addedit-empty-orders">
                              <p className="text-muted mb-0">No orders found for this user.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </Row>
                  </CardBody>

                  <div className="row justify-content-end gx-3 mt-3">
                    <div className="col-6 col-md-auto mb-2">
                      <button
                        className="btn btn-primary me-3 button-primary w-100 w-md-auto"
                        type="submit"
                        onClick={handleSubmit}
                        disabled={loading} // Disable button while loading
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          isEditMode ? "Update User" : "Add User"
                        )}
                      </button>
                    </div>
                    <div className="col-6 col-md-auto mb-2">
                      <button type="button" onClick={() => { previousPage == '/team' ? navigate("/team") : navigate("/user-list"); }} className="btn button-secondary btn-danger w-100 w-md-auto addedit-cancel-btn">Cancel</button>
                    </div>
                  </div>
                </form>

              </CardBody>

            </Card>
          </Col>
        </Row>
      </Container >

      <Modal isOpen={showAdminConfirmModal} toggle={dismissAdminConfirm} centered={true} size="sm">
        <button
          onClick={dismissAdminConfirm}
          type="button"
          className="close position-absolute confirm-modal-close"
          data-dismiss="modal"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <div className="modal-body pt-4">
          <div className="text-center mb-3">
            <i className="mdi mdi-alert-circle-outline confirm-modal-icon" />
            <h5 className="mt-2">Are you sure?</h5>
            <p>You are about to {isEditMode ? "update this user as" : "create"} an Admin for {agency?.data?.agency_name || "this account"}.</p>
          </div>
          <hr className="my-3" />
          <Row className="g-2 justify-content-between">
            <Col xs={6} md="auto">
              <button
                type="button"
                className="btn btn-primary button-primary w-100"
                onClick={confirmAdminPermission}
              >
                <i className="mdi mdi-check me-1"></i>
                Yes, proceed!
              </button>
            </Col>
            <Col xs={6} md="auto">
              <button
                type="button"
                className="btn btn-danger button-secondary confirm-modal-cancel w-100"
                onClick={dismissAdminConfirm}
              >
                <i className="mdi mdi-close me-1"></i>
                Cancel
              </button>
            </Col>
          </Row>
        </div>
      </Modal>

      <CrossSectionSyncModal
        isOpen={!!syncModalState}
        metricLabel={syncModalState?.metricLabel ?? ""}
        action={syncModalState?.action ?? "select"}
        originLabel={syncModalState?.originLabel ?? ""}
        targets={syncModalState?.targets ?? []}
        onConfirm={handleSyncConfirm}
        onDismiss={handleSyncDismiss}
        onCancel={() => setSyncModalState(null)}
      />

    </div >

  );
};

export default AddEditUser;
